/**
 * Hook para gerenciar configuração do widget de suporte interno
 * 
 * Busca e atualiza a configuração do widget que aparece em todas as páginas do tenant.
 * A configuração é armazenada em system_settings com key='support_widget'.
 * 
 * @author Sistema
 * @version 1.0.0
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { createLogger } from '@/lib/logger';

// Logger específico para este hook
const logger = createLogger('SupportWidgetConfig');

/**
 * Tipagem para a configuração do widget de suporte
 */
export interface SupportWidgetSettings {
  enabled: boolean;
  tenant_id: string | null;
  inbox_id: string | null;
  tenant_name: string | null;
  inbox_name: string | null;
  // Habilita botão de acesso remoto (WebRTC) no chat do tenant de suporte
  remote_access_enabled: boolean;
}

/**
 * Valores padrão quando não há configuração salva
 */
const DEFAULT_SETTINGS: SupportWidgetSettings = {
  enabled: false,
  tenant_id: null,
  inbox_id: null,
  tenant_name: null,
  inbox_name: null,
  remote_access_enabled: false, // Padrão: desabilitado
};

/**
 * Hook para buscar e atualizar configuração do widget de suporte
 * 
 * @returns Objeto com configuração atual, estado de loading e função de atualização
 */
export function useSupportWidgetConfig() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Query para buscar configuração do widget de suporte
  const { data: config, isLoading, error, refetch } = useQuery({
    queryKey: ['system-settings', 'support_widget'],
    queryFn: async (): Promise<SupportWidgetSettings> => {
      logger.debug('Buscando configuração do widget de suporte');
      
      // Server-side: substitui SELECT direto em system_settings por RPC
      const { data, error } = await supabase.rpc('get_support_widget_settings');

      if (error) {
        logger.error('Erro ao buscar configuração', error);
        throw error;
      }

      // RPC retorna jsonb (ou null quando não há registro)
      if (!data) {
        logger.debug('Nenhuma configuração encontrada, usando default');
        return DEFAULT_SETTINGS;
      }

      // Cast seguro para o tipo esperado
      const value = data as unknown as SupportWidgetSettings;
      return value || DEFAULT_SETTINGS;
    },
    staleTime: 5 * 60 * 1000, // Cache de 5 minutos
  });

  // Mutation para atualizar/criar configuração do widget
  const updateConfig = useMutation({
    mutationFn: async (newConfig: Partial<SupportWidgetSettings>) => {
      logger.info('Atualizando configuração do widget', newConfig);
      
      const updated = { ...(config || DEFAULT_SETTINGS), ...newConfig };
      
      // Usa upsert para criar ou atualizar
      const { error } = await supabase
        .from('system_settings')
        .upsert(
          { key: 'support_widget', value: updated },
          { onConflict: 'key' }
        );
      
      if (error) {
        logger.error('Erro ao salvar configuração', error);
        throw error;
      }
      
      logger.info('Configuração salva com sucesso');
      return updated;
    },
    onSuccess: (data) => {
      // Atualiza cache imediatamente
      queryClient.setQueryData(['system-settings', 'support_widget'], data);
      toast({
        title: 'Configuração salva',
        description: 'Widget de suporte configurado com sucesso.',
      });
    },
    onError: (error: Error) => {
      logger.error('Falha na mutation', error);
      toast({
        title: 'Erro ao salvar',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    config: config || DEFAULT_SETTINGS,
    isLoading,
    error,
    refetch,
    updateConfig: updateConfig.mutateAsync,
    isUpdating: updateConfig.isPending,
  };
}
